import { useCallback } from 'react'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import { useMutation } from 'react-query'
import BackNavLink from 'core/components/navigation/BackNavLink'
import APIService from 'api/APIService'
import { ErrorMessage } from 'types/entity'
import { AuthFormValue } from 'pages/auth/type'
import AnalyticsHelper from 'utils/AnalyticsHelper'
import useNavigation from 'core/hooks/useNavigation'
import {
  ERROR_PASSWORD_MAX_LENGTH,
  ERROR_PASSWORD_MIN_LENGTH,
  ERROR_PASSWORD_NOT_JUST_WHITESPACE,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH
} from 'pages/account/profile/validate'
import AsyncButton from 'core/components/buttons/AsyncButton'
import SmartField from 'core/components/form/SmartField'
import Input from 'core/components/inputs/Input'
import { useLocation, useParams } from 'react-router-dom'
import FeatureIcon from 'core/components/icons/FeatureIcon'
import { setAuthTokenCookie } from 'utils/AppHelper'

const TermsAndCondition = () => (
  <span className="flex justify-center pt-5">
    By signing up you agree to the
    <a
      className="pl-1 underline"
      target="_blank"
      href="https://everlance.com/terms-of-service"
      rel="noreferrer"
    >
      Terms & Conditions
    </a>
  </span>
)

const SignupPage = () => {
  const { search } = useLocation()
  const { replace } = useNavigation()
  const params = useParams()
  const signUpType = params?.type
  const searchParams = new URLSearchParams(search)
  const gt = searchParams.get('gt')
  const group_company_name = searchParams.get('group_company_name')
  const group_image_url = searchParams.get('group_image_url')
  const invite_token = searchParams.get('invite_token')
  const redirect_url = searchParams.get('redirect_url')
  const { control, handleSubmit } = useFormContext<AuthFormValue>()

  const { mutate, isLoading, isError, error } = useMutation(
    APIService.users.createUser,
    {
      onSuccess: (user) => {
        const { received_invites, auth_token } = user
        setAuthTokenCookie(auth_token)

        if (group_company_name) {
          AnalyticsHelper.trackEvent('Sign Up', {
            'Company Name': group_company_name
          })
          replace('/step-one')
          return
        }

        if (invite_token || received_invites.length > 0) {
          AnalyticsHelper.trackEvent('Sign Up', {
            'Invite Code': invite_token ?? received_invites[0].token_id
          })
          replace('/accept-invite')
          return
        }

        if (redirect_url === '/create-team') {
          AnalyticsHelper.trackEvent('Sign Up')
          replace('/create-team')
          return
        }

        AnalyticsHelper.trackEvent('Sign Up')
        replace('/about-you')
      }
    }
  )

  const onSubmit: SubmitHandler<AuthFormValue> = useCallback(
    (formData) => {
      mutate({
        mixpanel_distinct_id: window.mixpanel?.get_distinct_id?.(),
        gt,
        ...formData
      })
    },
    [gt, mutate]
  )

  const title =
    signUpType === 'accountant' ? 'Accountant Sign Up' : 'Create New Account'

  return (
    <div
      title="signup-page-container"
      className="h-[88vh] flex justify-around items-center"
    >
      <div className="card bg-white mb-5 !p-[6vmin] w-[500px]">
        <div className="mb-5">
          <BackNavLink to="/auth" />
        </div>
        <div className="flex justify-center">
          {group_company_name && group_image_url ? (
            <img
              src={group_image_url}
              className="h-20 border-none"
              alt={`${group_company_name} logo`}
            />
          ) : (
            <FeatureIcon name="everlance" />
          )}
        </div>
        <div className="flex justify-center">
          <h3 className="pb-20">{title}</h3>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SmartField
            controllerProps={{
              control,
              name: 'email',
              rules: {
                required: 'Email is required',
                validate: {
                  noBlank: (value) =>
                    value.trim().length > 0 || 'Email cannot be just whitespace'
                }
              }
            }}
          >
            <Input
              placeholder="email@email.com"
              className="text-lg mb-2 w-full py-3.5"
            />
          </SmartField>
          <SmartField
            controllerProps={{
              control,
              name: 'password',
              rules: {
                required: 'Password is required',
                validate: {
                  noBlank: (value) =>
                    value.trim().length > 0 ||
                    ERROR_PASSWORD_NOT_JUST_WHITESPACE,
                  minLength: (value) =>
                    value.length >= PASSWORD_MIN_LENGTH ||
                    ERROR_PASSWORD_MIN_LENGTH,
                  maxLength: (value) =>
                    value.length <= PASSWORD_MAX_LENGTH ||
                    ERROR_PASSWORD_MAX_LENGTH.format({
                      charsTyped: value.length
                    })
                }
              }
            }}
          >
            <Input
              type="password"
              placeholder="Enter your password"
              className="text-lg mb-2 w-full py-3.5"
            />
          </SmartField>
          {isError && (
            <p className="text-danger">
              {(error as ErrorMessage)?.response?.data?.message ??
                'An unknown error occurred'}
            </p>
          )}
          <AsyncButton
            variant="primary"
            status={isLoading ? 'loading' : 'idle'}
            type="submit"
            title="submit-button"
            className="w-full"
          >
            Sign Up
          </AsyncButton>
        </form>
        <TermsAndCondition />
      </div>
    </div>
  )
}

export default SignupPage
